<template>
  <div class="checklist-items">
    <div v-for="checklist in itemsToDisplay" :key="checklist.id">
      <div class="checklist-items__row-entity-name">
        <input v-model="checklist.title" @change="handleChecklistNameChange(checklist)" @focus="($event.target as HTMLInputElement).select()">
        <div class="checklist-items__row-entity-name__chicklets">
          <i class="ddi ddi-trash" @click.exact="handleChecklistDeleteClick(checklist)" />
        </div>
      </div>
      <div  class="checklist-items__list">
        <draggable
          :animation="180"
          :scroll-sensitivity="100"
          :scroll-speed="20"
          :force-fallback="true"
          :list="checklist.checklistItems"
          :draggable="'.draggable-item'"
          :handle="'.drag-handle'"
          drag-class="drag-card"
          item-key="id"
          @change="handleMovedItem($event, checklist)"
        >
          <template #item="{ element: item, index: itemIndex }">
            <div :class="item.isDone ? 'checklist-items__row-item' : 'checklist-items__row-item draggable-item'">
              <dd-checkbox v-model="item.isDone" dd-size="large" @change="handleCheckboxItemDoneClick(checklist, item, itemIndex)">&nbsp;</dd-checkbox>
              <span v-if="item.isDone" class="checklist-items__row-item__labeldone">{{item.name}}</span>
              <!-- data-replicated-value feeds the hidden copy of the name that sizes the textarea (see the label styles) -->
              <span v-else :data-replicated-value="item.name" class="checklist-items__row-item__label">
                <textarea
                  :ref="`itemInputs${checklist.id}_${item.id}`"
                  v-model="item.name"
                  rows="1"
                  maxlength="150"
                  @change="handleChecklistItemNameChange(checklist, item)"
                  @focus="($event.target as HTMLInputElement).select()"
                  @keydown.enter="handleChecklistItemEnterClick(checklist, itemIndex, $event)"
                />
              </span>
              <div class="checklist-items__row-item__chicklets">
                <span v-show="item.isDone" :title="new Date(item.doneOn).toLocaleString()" class="checklist-items__row-item__chicklets__donedate">
                  {{getDoneDate(item.doneOn)}}
                </span>
                <span v-show="item.isDone"><avatar :person="item.doer" :size="21" /></span>
                <span v-show="!item.isDone" class="drag-handle" @mousedown.prevent><i class="ddi ddi-sort"/></span>
                <span><i class="ddi ddi-trash" @click="handleChecklistItemDeleteClick(checklist, item)"/></span>
              </div>
            </div>
          </template>
        </draggable>
        <div class="checklist-items__add-link" @click="handleChecklistItemAddClick(checklist)">
          + Add Item
        </div>
      </div>
    </div>

    <div v-if="itemsToDisplay.length === 0" class="checklist-items__add-link">
      <checklist-picker 
        :project-id="projectID" 
        :item-id="taskID" 
        :is-linked-to-task="isLinkedToTask"
        @checklist-created="(checklist) => $emit('checklist-created', checklist)" 
      >
        <template #button>
          + Add Checklist
        </template>
      </checklist-picker>
    </div>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */

import TasksAPI from '@/api/modules/tasks.api'

/* Data types */
import { IChecklist, IChecklistItem } from '@/api/types/response.types'
import avatar from '../global/avatar.vue'
import draggable from 'vuedraggable'

export interface MoveEvent {
  moved: {
    element: IChecklistItem
    oldIndex: number
    newIndex: number
  }
}

/* API */

export default defineComponent({
  name: 'ChecklistItemsList',
  components: { avatar, draggable },

  props: {
    checklists: {
      type: Array as () => IChecklist[],
      default: (): IChecklist[] => [],
    },
    isLinkedToTask: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['checklist-created', 'checklist-deleted'],

  data() {
    return {
      // Read once, not computed from $route, which has the next page's params by the time a late save finishes
      projectID: Number(this.$route.params.id),
      taskID: Number(this.$route.params.childId),
      showAllItems: false,
      showModal: false,
    }
  },

  computed: {
    itemsToDisplay(): IChecklist[] {
      return this.checklists
    },
  },

  created() {
    this.sortChecklistItems()
  },

  methods: {
    handleChecklistItemAddClick(checklist: IChecklist) {
      const response = TasksAPI.post_ChecklistItemCreate(
        this.projectID,
        this.taskID,
        checklist.id,
        'New Item',
      ).then(newItem => {
        if (!checklist.checklistItems) {
          checklist.checklistItems = []
        }
        checklist.checklistItems.push(newItem)
        this.$nextTick(() => {
          // An array when the ref is inside a v-for
          const itemRef = this.$refs[`itemInputs${checklist.id}_${newItem.id}`]
          const itemInput = (Array.isArray(itemRef) ? itemRef[0] : itemRef) as HTMLTextAreaElement
          itemInput.focus()
        })
      })
    },
    handleChecklistItemEnterClick(
      checklist: IChecklist,
      itemIndex: number,
      event: KeyboardEvent,
    ) {
      // Leave Enter alone while it confirms IME composition (keyCode 229)
      if (event.keyCode === 229) {
        return
      }

      // Names are one line, so Enter finishes the item rather than adding a line break
      event.preventDefault()
      if (itemIndex === checklist.checklistItems.length - 1) {
        this.handleChecklistItemAddClick(checklist)
      } else {
        const itemInput = event.target as HTMLTextAreaElement
        itemInput.blur()
      }
    },
    handleCheckboxItemDoneClick(
      checklist: IChecklist,
      item: IChecklistItem,
      itemIndex: number,
    ) {
      const response = TasksAPI.put_ChecklistItemUpdate(
        this.projectID,
        this.taskID,
        checklist.id,
        item.id,
        {
          name: null,
          isDone: item.isDone,
        },
      ).then(updatedItem => {
        item.isDone = updatedItem.isDone
        item.doneOn = updatedItem.doneOn
        item.doer = updatedItem.doer
        if (item.isDone) {
          // Move to end of list
          checklist.checklistItems.push(
            checklist.checklistItems.splice(itemIndex, 1)[0],
          )
        } else {
          this.sortChecklistItems()
        }
      })
    },
    handleChecklistItemNameChange(checklist: IChecklist, item: IChecklistItem) {
      // A pasted line break would otherwise end up in the name
      item.name = item.name.replace(/[\r\n]+/g, ' ')
      const response = TasksAPI.put_ChecklistItemUpdate(
        this.projectID,
        this.taskID,
        checklist.id,
        item.id,
        {
          name: item.name,
          isDone: null,
        },
      )
    },
    handleChecklistItemDeleteClick(
      checklist: IChecklist,
      item: IChecklistItem,
    ) {
      this.$confirm(`${item.name}`, {
        center: true,
        confirmButtonText: 'Yes, Delete',
        confirmButtonClass: 'el-button--strawberry',
        cancelButtonText: 'Cancel',
        cancelButtonClass: 'el-button--text',
        type: 'warning',
        title: 'Are you sure you want to delete this item?',
      }).then(() => {
        const index = checklist.checklistItems.indexOf(item)
        if (index > -1) {
          checklist.checklistItems.splice(index, 1)
          const response = TasksAPI.delete_ChecklistItemDelete(
            this.projectID,
            this.taskID,
            checklist.id,
            item.id,
          )
        }
      })
    },
    handleChecklistNameChange(checklist: IChecklist) {
      TasksAPI.put_ChecklistUpdate(
        this.projectID,
        this.taskID,
        checklist.id,
        checklist.title,
      )
    },
    handleChecklistDeleteClick(checklist: IChecklist) {
      this.$confirm(`${checklist.title}`, {
        center: true,
        confirmButtonText: 'Yes, Delete',
        confirmButtonClass: 'el-button--strawberry',
        cancelButtonText: 'Cancel',
        cancelButtonClass: 'el-button--text',
        type: 'warning',
        title: 'Are you sure you want to delete this checklist?',
      })
        .then(() => {
          return TasksAPI.delete_ChecklistDelete(
            this.projectID,
            this.taskID,
            checklist.id,
          )
        })
        .then(() => {
          this.$emit('checklist-deleted', checklist)
        })
    },
    handleMovedItem(event: MoveEvent, checklist: IChecklist) {
      TasksAPI.put_ChecklistItemUpdateSort(
        this.projectID,
        this.taskID,
        checklist.id,
        checklist.checklistItems.map<number>(item => item.id),
      )
    },
    sortChecklistItems() {
      this.checklists.forEach(checklist => {
        if (checklist.checklistItems) {
          checklist.checklistItems.sort((a, b) => {
            if (a.isDone === b.isDone) {
              return a.sort - b.sort
            }

            return a.isDone ? 1 : -1
          })
        }
      })
    },
    getDoneDate(dateString: string): string {
      const date = new Date(dateString)
      return (
        date.toLocaleDateString('default', { month: 'short' }) +
        ' ' +
        date.getDate()
      )
    },
  },
})
</script>

<style lang="scss">
.el-checkbox {
  margin-bottom: 0;

  &__label {
    font-size: 13px !important;
    font-weight: normal;
    color: $color--gray-90 !important;
  }
}

.drag-card {
  cursor: grab !important;
  opacity: 0.95 !important;
  background-color: var(--color-background-primary) !important;
  border: solid 1px $light-blue-grey-two;
}

.checklist-items {
  border: solid 1px $light-blue-grey-two;
  border-radius: 8px;
  min-height: 40px;

  &__list {
    overflow: auto;
    max-height: 250px;
  }

  &__add-link {
    padding: 8px 12px;
    cursor: pointer;
    @include lettering--p-sm;
    color: var(--color-text-primary);
  }

  &__row-entity-name {
    background-color: var(--color-background-secondary);
    padding: 6px 12px;
    border-bottom: solid 1px $light-blue-grey-two;
    @include lettering--h5;
    color: var(--color-text-third);

    &__chicklets {
      float: right;
      cursor: pointer;
      text-transform: none;
      font-size: 15px;
      color: var(--color-text-primary);
    }

    input {
      width: 90%;
      background-color: var(--color-background-secondary);
      border-bottom: solid 1px #ced5d6;
      font-size: 11px !important;
      font-weight: 500;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.45;
      letter-spacing: 0.4px;
      text-transform: uppercase;
      border: none;

      color: var(--color-text-primary);
      &:focus {
        outline: solid 0px;
      }
    }
  }

  &__row-item {
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    padding: 7px 12px;
    border-bottom: solid 1px $light-blue-grey-two;
    @include lettering--p;

    &__label {
      width: 100%;
      border: none;
      /* The textarea shares a grid cell with a hidden copy of its text (::after). The copy sizes the cell,
         so the textarea grows to fit names that wrap. */
      display: grid;
      grid-template-columns: minmax(0, 1fr);

      &::after {
        content: attr(data-replicated-value) ' ';
        visibility: hidden;
        white-space: pre-wrap;
      }

      textarea,
      &::after {
        grid-area: 1 / 1;
        padding: 2px 0px 2px 0px;
        font: inherit;
        word-break: break-word;
      }

      textarea {
        width: 100%;
        border: none;
        resize: none;
        overflow: hidden;
        color: var(--color-text-primary);
        background-color: var(--color-input-background);
        &:focus {
          outline: solid 0px;
        }
      }
    }

    &__labeldone {
      text-decoration: line-through;
      word-break: break-word;
    }

    &:hover {
      background-color: var(--color-background-hover);
    }

    &:focus-within {
      background-color: var(--color-background-hover);
    }

    > span {
      padding-right: 8px;
      margin-right: auto;
      @include lettering--p-sm;
    }

    &__chicklets {
      display: flex;
      /* Long names wrap instead of squeezing the date and icons */
      flex-shrink: 0;
      align-items: center;
      @include flex-row-horizontal-spacing(8px);

      i {
        cursor: pointer;
      }

      &__donedate {
        font-size: 12px;
        white-space: nowrap;
      }
    }

    &__unlink {
      color: var(--color-text-third);
      margin-left: 4px;
    }

    > .ddi {
      color: $color--blue;
      padding-right: 4px;
    }

    > .ddi-task-locked,
    > .ddi-conversation-locked {
      color: $color--gray-60 !important;
    }
  }
}
</style>
